(() => {
  if (window.__rocketzLicenseRuntime) return;
  window.__rocketzLicenseRuntime = true;

  const script = document.currentScript;
  const designMode = script?.dataset.designMode === "true";
  const themeBuild = script?.dataset.themeBuild || "unknown";
  const shop = window.Shopify?.shop || window.location.hostname;
  const cacheKey = `rz-license-v3:${shop}`;
  const activeCacheTtl = 900000;
  const activePollEvery = 900000;
  const inactivePollEvery = 60000;
  const offlineGrace = 172800000;
  const firstVisitGrace = 300000;
  const affiliateCacheKey = "rz-affiliate-v1";
  const affiliateCacheTtl = 10368000000;
  let checking = false;
  let pollTimer;

  const captureAffiliateAttribution = async () => {
    const params = new URLSearchParams(window.location.search);
    const queryReferral = params.get("rz_ref")?.trim().toUpperCase() || "";
    const queryLink = params.get("rz_link")?.trim() || "";
    const queryTimestamp = Number(params.get("rz_at"));
    const validReferral = /^[A-Z0-9_-]{3,48}$/.test(queryReferral);
    const validLink = /^[A-Za-z0-9_-]{3,48}$/.test(queryLink);
    let attribution = null;

    if (validReferral && validLink) {
      attribution = {
        referral: queryReferral,
        link: queryLink,
        clickedAt: Number.isFinite(queryTimestamp) && queryTimestamp > 0 ? queryTimestamp : Date.now(),
        expiresAt: Date.now() + affiliateCacheTtl,
      };
      try {
        localStorage.setItem(affiliateCacheKey, JSON.stringify(attribution));
      } catch {}
      params.delete("rz_ref");
      params.delete("rz_link");
      params.delete("rz_at");
      const cleanQuery = params.toString();
      history.replaceState(history.state, "", `${window.location.pathname}${cleanQuery ? `?${cleanQuery}` : ""}${window.location.hash}`);
    } else {
      try {
        attribution = JSON.parse(localStorage.getItem(affiliateCacheKey) || "null");
      } catch {
        attribution = null;
      }
    }

    const validCachedAttribution =
      attribution &&
      /^[A-Z0-9_-]{3,48}$/.test(attribution.referral || "") &&
      /^[A-Za-z0-9_-]{3,48}$/.test(attribution.link || "") &&
      Number(attribution.expiresAt) > Date.now();
    if (!validCachedAttribution) {
      try {
        localStorage.removeItem(affiliateCacheKey);
      } catch {}
      return;
    }

    const syncKey = `rz-affiliate-synced:${attribution.referral}:${attribution.link}`;
    try {
      if (sessionStorage.getItem(syncKey) === "1") return;
    } catch {}

    try {
      const response = await fetch("/cart/update.js", {
        method: "POST",
        credentials: "same-origin",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({
          attributes: {
            _rocketz_affiliate: attribution.referral,
            _rocketz_affiliate_link: attribution.link,
            _rocketz_affiliate_at: String(attribution.clickedAt),
          },
        }),
      });
      if (response.ok) sessionStorage.setItem(syncKey, "1");
    } catch {}
  };

  let memoryState;
  let failureCount = 0;
  const readCache = () => {
    if (memoryState) return memoryState;
    try {
      const stored = JSON.parse(localStorage.getItem(cacheKey) || "null");
      if (stored?.shop === shop && typeof stored.active === "boolean") memoryState = stored;
      if (!memoryState) {
        const legacy = JSON.parse(localStorage.getItem("rz-license-v2") || "null");
        if (legacy?.active === true && Number.isFinite(legacy.checkedAt) && legacy.checkedAt <= Date.now()) {
          memoryState = { ...legacy, shop };
        }
      }
    } catch {}
    return memoryState || null;
  };
  const saveCache = (state) => {
    memoryState = state;
    try { localStorage.setItem(cacheKey, JSON.stringify(state)); } catch {}
  };

  const renderBlockedScreen = (state) => {
    document.querySelector(".rz-license-blocked-screen")?.remove();
    if (state.active || state.pending) return;
    const unavailable = state.reason === "verification_unavailable";
    const screen = document.createElement("main");
    screen.className = "rz-license-blocked-screen";
    screen.setAttribute("role", "main");
    const content = document.createElement("div");
    content.className = "rz-license-blocked-screen__content";
    const title = document.createElement("h1");
    title.textContent = unavailable ? "Verificação temporariamente indisponível" : "Tema sem licença ativa";
    const message = document.createElement("p");
    message.textContent = unavailable
      ? "Não foi possível verificar a loja. Tentaremos novamente automaticamente em instantes."
      : designMode
        ? "Abra o aplicativo Rocketz no admin da Shopify para ativar ou transferir a licença desta loja."
        : "Esta loja precisa de uma licença Rocketz ativa. Entre em contato com o responsável pela loja.";
    content.append(title, message);
    screen.appendChild(content);
    document.body.appendChild(screen);
  };
  const apply = (state) => {
    document.documentElement.dataset.rocketzLicense = state.pending ? "pending" : state.active ? "active" : "inactive";
    renderBlockedScreen(state);
    document.dispatchEvent(new CustomEvent("rocketz:license", { detail: state }));
  };
  const cacheAge = (state) => Date.now() - Number(state?.checkedAt || 0);
  const hasActiveCache = (state, ttl) => state?.active === true && Number.isFinite(state.checkedAt) &&
    cacheAge(state) >= 0 && cacheAge(state) < ttl && (!state.expiresAt || Date.parse(state.expiresAt) > Date.now());
  const isFreshActiveCache = (state) => hasActiveCache(state, activeCacheTtl);
  const scheduleCheck = (delay) => {
    clearTimeout(pollTimer);
    pollTimer = setTimeout(checkLicense, delay);
  };
  const denialReasons = new Set(["invalid_shop", "shop_not_connected", "shop_not_found", "shop_inactive",
    "no_license", "license_not_found", "license_pending", "license_suspended", "license_expired", "license_revoked"]);
  const checkLicense = async () => {
    if (checking) return;
    checking = true;
    let nextDelay = inactivePollEvery;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const query = new URLSearchParams({ theme_build: themeBuild, _rz: String(Date.now()) });
      const response = await fetch(`/apps/rocketz-license?${query}`, {
        credentials: "same-origin",
        headers: { Accept: "application/json", "Cache-Control": "no-cache" },
        cache: "no-store", signal: controller.signal,
      });
      if (!response.ok) throw new Error("verification_unavailable");
      const payload = await response.json();
      if (!payload || typeof payload.active !== "boolean" ||
        (payload.active ? payload.reason !== "ok" : !denialReasons.has(payload.reason)) ||
        (payload.expiresAt != null && !Number.isFinite(Date.parse(payload.expiresAt)))) {
        throw new Error("invalid_verification_response");
      }
      const state = {
        shop, active: payload.active, reason: payload.reason,
        entitlements: Array.isArray(payload.entitlements) ? payload.entitlements : [],
        expiresAt: payload.expiresAt || null, checkedAt: Date.now(),
      };
      saveCache(state);
      apply(state);
      failureCount = 0;
      nextDelay = state.active ? activePollEvery : inactivePollEvery;
      if (state.active && state.expiresAt) nextDelay = Math.max(1000, Math.min(nextDelay, Date.parse(state.expiresAt) - Date.now()));
    } catch {
      failureCount++;
      const lastKnown = readCache();
      if (hasActiveCache(lastKnown, offlineGrace)) {
        apply({ ...lastKnown, reason: "offline_grace" });
      } else if (lastKnown?.active === true && lastKnown.expiresAt && Date.parse(lastKnown.expiresAt) <= Date.now()) {
        apply({ ...lastKnown, active: false, reason: "license_expired" });
      } else if (lastKnown?.active === false && denialReasons.has(lastKnown.reason)) {
        apply(lastKnown);
      } else {
        const failureStartedAt = Number.isFinite(lastKnown?.failureStartedAt) && lastKnown.failureStartedAt <= Date.now()
          ? lastKnown.failureStartedAt : Date.now();
        const state = { ...lastKnown, shop, active: false, reason: "verification_unavailable", entitlements: [], failureStartedAt };
        saveCache(state);
        apply({ ...state, pending: !lastKnown?.checkedAt && Date.now() - failureStartedAt < firstVisitGrace });
      }
      nextDelay = failureCount === 1 ? 2000 : failureCount === 2 ? 10000 : inactivePollEvery;
    } finally {
      clearTimeout(timeout);
      checking = false;
      scheduleCheck(nextDelay);
    }
  };
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && !isFreshActiveCache(readCache())) checkLicense();
  });
  window.addEventListener("pageshow", (event) => {
    if (event.persisted && !isFreshActiveCache(readCache())) checkLicense();
  });
  window.addEventListener("online", checkLicense);
  const cached = readCache();
  if (hasActiveCache(cached, offlineGrace)) apply(cached);
  if (isFreshActiveCache(cached)) {
    const remaining = Math.min(activeCacheTtl - cacheAge(cached), cached.expiresAt ? Date.parse(cached.expiresAt) - Date.now() : activeCacheTtl);
    scheduleCheck(Math.max(remaining, 1000));
  } else {
    checkLicense();
  }

  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(captureAffiliateAttribution, { timeout: 3000 });
  } else {
    window.setTimeout(captureAffiliateAttribution, 1000);
  }
})();
